import React from 'react';
import { Clock, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

const ProviderOverview = ({ provider, recentOutages = [] }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'text-green-600 bg-green-100';
      case 'investigating': return 'text-yellow-600 bg-yellow-100';
      case 'identified': return 'text-orange-600 bg-orange-100';
      case 'monitoring': return 'text-blue-600 bg-blue-100';
      default: return 'text-red-600 bg-red-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Provider Information */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Provider Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Service Category</h4>
              <p className="text-gray-900 capitalize">{provider?.category?.replace('_', ' ')}</p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Status</h4>
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${
                provider?.status === 'active' ? 'text-green-800 bg-green-100' :
                provider?.status === 'maintenance'? 'text-yellow-800 bg-yellow-100' : 'text-red-800 bg-red-100'
              }`}>
                {provider?.status}
              </span>
            </div>
            
            {provider?.support_url && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Support</h4>
                <a
                  href={provider?.support_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Support Center
                </a>
              </div>
            )}
            
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Last Updated</h4>
              <p className="text-gray-900">
                {provider?.updated_at ? format(new Date(provider?.updated_at), 'MMM dd, yyyy') : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Current Status */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Status</h3>
        
        <div className="space-y-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-900">Operational</p>
              <p className="text-xs text-gray-500">All systems normal</p>
            </div>
          </div>
          
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Active Incidents</h4>
            {recentOutages?.filter(outage => outage?.status !== 'resolved')?.length > 0 ? (
              <div className="space-y-2">
                {recentOutages?.filter(outage => outage?.status !== 'resolved')?.slice(0, 3)?.map((outage) => (
                  <div key={outage?.id} className="flex items-center text-sm">
                    <AlertTriangle className="h-4 w-4 text-orange-500 mr-2" />
                    <span className="text-gray-700 truncate">{outage?.title}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No active incidents</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Outages */}
      <div className="lg:col-span-3">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Outages</h3>
          
          {recentOutages?.length > 0 ? (
            <div className="space-y-4">
              {recentOutages?.map((outage) => (
                <div key={outage?.id} className="border-l-4 border-blue-200 pl-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{outage?.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{outage?.description}</p>
                      
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(outage?.status)}`}>
                          {outage?.status}
                        </span>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getSeverityColor(outage?.severity)}`}>
                          {outage?.severity}
                        </span>
                        <div className="flex items-center text-xs text-gray-500">
                          <Clock className="h-3 w-3 mr-1" />
                          {format(new Date(outage?.created_at), 'MMM dd, HH:mm')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-500">No recent outages reported</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderOverview;